import { FormEvent, useCallback, useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  createLimitsEnrichItem,
  updateLimitsEnrichItem,
  type LimitsEnrichItem,
} from "../../api/limitsEnrichItem";
import { useNotification } from "../Notification/NotificationProvider";
import { ModalPortal } from "../ModalPortal/ModalPortal";
import { CircularProgress } from "../CircularProgress/CircularProgress";
import { FormDropdown } from "../FormDropdown/FormDropdown";
import {
  CHECK_TYPE_OPTIONS,
  type EnrichForm,
  emptyEnrichForm,
  toEnrichPayload,
  enrichToForm,
  validateEnrichForm,
} from "./limitsEnrichItemForm";
import "../CheckModal/CheckModal.scss";
import "../CategoryModal/CategoryModal.scss";

interface LimitsEnrichItemModalProps {
  item?: LimitsEnrichItem | null;
  onClose: () => void;
  onSaved: (item: LimitsEnrichItem) => void;
}

export const LimitsEnrichItemModal = ({
  item = null,
  onClose,
  onSaved,
}: LimitsEnrichItemModalProps) => {
  const { showNotification } = useNotification();
  const isCreate = !item?.id;
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreate);
  const [form, setForm] = useState<EnrichForm>(
    item ? enrichToForm(item) : emptyEnrichForm()
  );

  useEffect(() => {
    if (!item?.id) {
      setForm(emptyEnrichForm());
      setIsEditing(true);
      return;
    }

    setForm(enrichToForm(item));
    setIsEditing(false);
  }, [item]);

  const readOnly = !isEditing;

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      event.preventDefault();
      const validationError = validateEnrichForm(form);
      if (validationError) {
        showNotification({ message: validationError, variant: "error" });
        return;
      }

      setSaving(true);
      const payload = toEnrichPayload(form);

      try {
        let saved: LimitsEnrichItem;
        if (isCreate) {
          saved = await createLimitsEnrichItem(payload);
          showNotification({ message: "Пакет докупки создан", variant: "success" });
        } else if (item?.id) {
          saved = await updateLimitsEnrichItem(item.id, payload);
          showNotification({ message: "Пакет докупки обновлён", variant: "success" });
        } else {
          return;
        }
        onSaved(saved);
        onClose();
      } catch (err: unknown) {
      } finally {
        setSaving(false);
      }
    },
    [form, isCreate, item?.id, onClose, onSaved, showNotification]
  );

  const title = isCreate
    ? "Создать пакет докупки"
    : isEditing
      ? "Редактировать пакет докупки"
      : "Пакет докупки";

  return (
    <ModalPortal>
      <div className="detail-checks-modal-overlay" onClick={onClose}>
        <div
          className="detail-checks-modal detail-checks-modal--check"
          onClick={(event) => event.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-label={title}
        >
          <div className="detail-checks-modal-head">
            <h3>{title}</h3>
            <button
              type="button"
              className="detail-tasklist-close"
              onClick={onClose}
              aria-label="Закрыть"
            >
              <X size={18} />
            </button>
          </div>

          <form
            id="limits-enrich-item-form"
            className="detail-checks-modal-body create-request-modal-form check-modal-form"
            onSubmit={handleSubmit}
          >
            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label">Тип проверки</label>
              <FormDropdown
                value={form.checkType}
                options={CHECK_TYPE_OPTIONS}
                disabled={readOnly || saving}
                onChange={(value) =>
                  setForm((current) => ({
                    ...current,
                      checkType: value as EnrichForm["checkType"],
                  }))
                }
              />
            </div>

            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label" htmlFor="limits-enrich-amount">
                Размер пакета
              </label>
              <input
                id="limits-enrich-amount"
                className="create-request-modal-input"
                type="number"
                min={10}
                step={1}
                value={form.amount}
                disabled={readOnly || saving}
                onChange={(event) =>
                  setForm((current) => ({ ...current, amount: event.target.value }))
                }
              />
            </div>

            <div className="create-request-modal-form-group">
              <label className="create-request-modal-label" htmlFor="limits-enrich-price">
                Цена пакета, ₽
              </label>
              <input
                id="limits-enrich-price"
                className="create-request-modal-input"
                type="number"
                min={0}
                step={1}
                value={form.price}
                disabled={readOnly || saving}
                onChange={(event) =>
                  setForm((current) => ({ ...current, price: event.target.value }))
                }
              />
            </div>

            <label
              className={`check-modal-active${readOnly || saving ? " check-modal-active--disabled" : ""}`}
            >
              <input
                type="checkbox"
                checked={form.active}
                disabled={readOnly || saving}
                onChange={(event) =>
                  setForm((current) => ({ ...current, active: event.target.checked }))
                }
              />
              <span>Активен</span>
            </label>
          </form>

          <div className="detail-checks-modal-footer category-modal-actions">
            {!isCreate && !isEditing && (
              <button
                type="button"
                className="category-modal-secondary-btn"
                onClick={() => setIsEditing(true)}
              >
                Редактировать
              </button>
            )}
            {!isCreate && isEditing && (
              <button
                type="button"
                className="category-modal-secondary-btn"
                disabled={saving}
                onClick={() => {
                  setIsEditing(false);
                    if (item) setForm(enrichToForm(item));
                }}
              >
                Отмена
              </button>
            )}
            {(isCreate || isEditing) && (
              <button
                type="submit"
                form="limits-enrich-item-form"
                className="create-request-modal-submit"
                disabled={saving}
              >
                {saving ? <CircularProgress size="sm" /> : isCreate ? "Создать" : "Сохранить"}
              </button>
            )}
          </div>
        </div>
      </div>
    </ModalPortal>
  );
};
